<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { aiApi } from '../lib/api';
  import type { AIAnalysisData, TimeSeriesData } from '../lib/types';
  import { formatDate } from '../lib/utils';
  
  export let timeseriesId: string;
  export let timeseriesData: TimeSeriesData | null = null;
  export let analyses: AIAnalysisData[] = [];
  export let isLoading: boolean = false;
  
  // Check if data is available (useful for future UI conditionals)
  $: dataAvailable = timeseriesData && timeseriesData.data_points && timeseriesData.data_points.length > 0;
  // Use the variable in a dummy block to prevent unused variable warning
  $: { void(dataAvailable); }
  
  const dispatch = createEventDispatcher();
  
  let showCreateForm = false;
  let formError = '';
  let selectedAnalysis: AIAnalysisData | null = null;
  let selectedAnalysisType = 'summary';
  let formSubmitting = false;
  
  // Select an analysis to display
  function selectAnalysis(analysis: AIAnalysisData) {
    if (selectedAnalysis?.id === analysis.id) {
      selectedAnalysis = null;
    } else {
      selectedAnalysis = analysis;
    }
  }
  
  // Create a new analysis
  async function createAnalysis() {
    try {
      formSubmitting = true;
      formError = '';
      
      // Call API to create analysis
      const newAnalysis = await aiApi.analyze(timeseriesId, selectedAnalysisType);
      
      // Add new analysis to list and select it
      analyses = [newAnalysis, ...analyses];
      selectedAnalysis = newAnalysis;
      
      // Dispatch event
      dispatch('analysisCreated', { analysis: newAnalysis });
      
      // Hide form
      showCreateForm = false;
    } catch (error) {
      formError = error instanceof Error ? error.message : 
                  typeof error === 'string' ? error : 'Failed to create analysis';
      console.error('Error creating analysis:', error);
    } finally {
      formSubmitting = false;
    }
  }
  
  // Format object for display
  function formatObject(obj: Record<string, unknown> | null | undefined): string {
    if (!obj) return 'Not available';
    return JSON.stringify(obj, null, 2);
  }
</script>

<div class="analysis-panel card mb-4">
  <div class="card-header d-flex justify-content-between align-items-center">
    <h5 class="mb-0">AI Analysis</h5>
    <div>
      {#if !showCreateForm}
        <button 
          class="btn btn-sm btn-primary" 
          on:click={() => showCreateForm = true}
          disabled={isLoading}
        >
          <i class="bi bi-magic me-1"></i>
          New Analysis
        </button>
      {:else}
        <button 
          class="btn btn-sm btn-outline-secondary" 
          on:click={() => showCreateForm = false}
        >
          <i class="bi bi-x-circle me-1"></i>
          Cancel
        </button>
      {/if}
    </div>
  </div>
  
  <div class="card-body">
    {#if formError}
      <div class="alert alert-danger alert-dismissible fade show" role="alert">
        Error creating analysis: {formError}
        <button type="button" class="btn-close" on:click={() => formError = ''} aria-label="Close"></button>
      </div>
    {/if}
    
    {#if showCreateForm}
      <div class="create-analysis-form mb-4">
        <h6 class="mb-3">Create New Analysis</h6>
        
        <form on:submit|preventDefault={createAnalysis}>
          <div class="mb-3">
            <label for="analysisType" class="form-label">Analysis Type</label>
            <select id="analysisType" class="form-select" bind:value={selectedAnalysisType}>
              <option value="summary">Time Series Summary</option>
              <option value="patterns">Pattern Detection</option>
              <option value="anomalies">Anomaly Detection</option>
            </select>
            <div class="form-text">
              {#if selectedAnalysisType === 'summary'}
                Provides an overview of the time series with key statistics and insights.
              {:else if selectedAnalysisType === 'patterns'}
                Detects recurring patterns, trends, and seasonality in the data.
              {:else if selectedAnalysisType === 'anomalies'}
                Identifies unusual data points and outliers in the time series.
              {/if}
            </div>
          </div>
          
          <div class="d-flex justify-content-end">
            <button type="submit" class="btn btn-primary" disabled={formSubmitting}>
              {#if formSubmitting}
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Analyzing...
              {:else}
                <i class="bi bi-magic me-2"></i>
                Start Analysis
              {/if}
            </button>
          </div>
        </form>
      </div>
    {/if}
    
    {#if isLoading && analyses.length === 0}
      <div class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2">Loading analyses...</p>
      </div>
    {:else if analyses.length === 0}
      <div class="text-center py-4">
        <i class="bi bi-lightbulb" style="font-size: 2rem; color: #adb5bd;"></i>
        <p class="mt-2">Анализы отсутствуют. Создайте анализ, чтобы получить информацию о ваших данных.</p>
      </div>
    {:else}
      <div class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th>Type</th>
              <th>Created</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {#each analyses as analysis}
              <tr 
                class={selectedAnalysis?.id === analysis.id ? 'table-info' : ''}
                on:click={() => selectAnalysis(analysis)}
                style="cursor: pointer;"
              >
                <td>
                  <div class="d-flex align-items-center">
                    <i class="bi {analysis.analysis_type === 'summary' ? 'bi-file-text' : 
                              analysis.analysis_type === 'patterns' ? 'bi-graph-up' : 
                              'bi-exclamation-triangle'} me-2"></i>
                    <span class="text-capitalize">{analysis.analysis_type}</span>
                  </div>
                </td>
                <td>{formatDate(analysis.created_at)}</td>
                <td>
                  <button class="btn btn-sm btn-outline-secondary">
                    {selectedAnalysis?.id === analysis.id ? 'Hide Details' : 'Show Details'}
                  </button>
                </td>
              </tr>
              
              {#if selectedAnalysis?.id === analysis.id}
                <tr class="analysis-details-row">
                  <td colspan="3">
                    <div class="analysis-details">
                      {#if analysis.analysis_type === 'summary' && analysis.results["summary"]}
                        <div class="card mb-3">
                          <div class="card-body">
                            <h6 class="card-title">Summary</h6>
                            <p class="card-text">{analysis.results["summary"]}</p>
                          </div>
                        </div>
                        
                        {#if analysis.results["statistics"]}
                          <div class="card mb-3">
                            <div class="card-body">
                              <h6 class="card-title">Statistics</h6>
                              <div class="row">
                                <div class="col-md-3 mb-2">
                                  <div class="stat-card">
                                    <span class="stat-title">Minimum</span>
                                    <span class="stat-value">{analysis.results["statistics"].min}</span>
                                  </div>
                                </div>
                                <div class="col-md-3 mb-2">
                                  <div class="stat-card">
                                    <span class="stat-title">Maximum</span>
                                    <span class="stat-value">{analysis.results["statistics"].max}</span>
                                  </div>
                                </div>
                                <div class="col-md-3 mb-2">
                                  <div class="stat-card">
                                    <span class="stat-title">Average</span>
                                    <span class="stat-value">{analysis.results["statistics"].average}</span>
                                  </div>
                                </div>
                                <div class="col-md-3 mb-2">
                                  <div class="stat-card">
                                    <span class="stat-title">Trend</span>
                                    <span class="stat-value text-capitalize">{analysis.results["statistics"].trend}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        {/if}
                        
                        {#if analysis.results["patterns"] && analysis.results["patterns"].length > 0}
                          <div class="card mb-3">
                            <div class="card-body">
                              <h6 class="card-title">Patterns</h6>
                              <ul class="mb-0">
                                {#each analysis.results["patterns"] as pattern}
                                  <li>{pattern}</li>
                                {/each}
                              </ul>
                            </div>
                          </div>
                        {/if}
                        
                        {#if analysis.results["anomalies"] && analysis.results["anomalies"].length > 0}
                          <div class="card mb-3">
                            <div class="card-body">
                              <h6 class="card-title">Anomalies</h6>
                              <ul class="mb-0">
                                {#each analysis.results["anomalies"] as anomaly}
                                  <li>{anomaly}</li>
                                {/each}
                              </ul>
                            </div>
                          </div>
                        {/if}
                      {:else if analysis.analysis_type === 'patterns'}
                        <div class="card mb-3">
                          <div class="card-body">
                            <h6 class="card-title">Overall Assessment</h6>
                            <p class="card-text">{analysis.results["overall_assessment"]}</p>
                          </div>
                        </div>
                        
                        {#if analysis.results["patterns"] && analysis.results["patterns"].length > 0}
                          <div class="card mb-3">
                            <div class="card-body">
                              <h6 class="card-title">Detected Patterns</h6>
                              <div class="table-responsive">
                                <table class="table table-sm">
                                  <thead>
                                    <tr>
                                      <th>Type</th>
                                      <th>Description</th>
                                      <th>Confidence</th>
                                      <th>Duration</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {#each analysis.results["patterns"] as pattern}
                                      {#if typeof pattern === 'object'}
                                        <tr>
                                          <td class="text-capitalize">{pattern.type}</td>
                                          <td>{pattern.description}</td>
                                          <td>{(pattern.confidence * 100).toFixed(1)}%</td>
                                          <td>{pattern.duration || 'N/A'}</td>
                                        </tr>
                                      {:else}
                                        <tr>
                                          <td colspan="4">{pattern}</td>
                                        </tr>
                                      {/if}
                                    {/each}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        {/if}
                      {:else if analysis.analysis_type === 'anomalies'}
                        <div class="card mb-3">
                          <div class="card-body">
                            <h6 class="card-title">Overall Assessment</h6>
                            <p class="card-text">{analysis.results["overall_assessment"]}</p>
                          </div>
                        </div>
                        
                        {#if analysis.results["anomalies"] && analysis.results["anomalies"].length > 0}
                          <div class="card mb-3">
                            <div class="card-body">
                              <h6 class="card-title">Detected Anomalies</h6>
                              <div class="table-responsive">
                                <table class="table table-sm">
                                  <thead>
                                    <tr>
                                      <th>Timestamp</th>
                                      <th>Value</th>
                                      <th>Type</th>
                                      <th>Severity</th>
                                      <th>Description</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {#each analysis.results["anomalies"] as anomaly}
                                      {#if typeof anomaly === 'object'}
                                        <tr>
                                          <td>{anomaly.timestamp}</td>
                                          <td>{anomaly.value}</td>
                                          <td class="text-capitalize">{anomaly.type}</td>
                                          <td class="text-capitalize">
                                            <span class="badge {anomaly.severity === 'high' ? 'bg-danger' : 
                                                             anomaly.severity === 'medium' ? 'bg-warning' : 
                                                             'bg-info'}">
                                              {anomaly.severity}
                                            </span>
                                          </td>
                                          <td>{anomaly.description}</td>
                                        </tr>
                                      {:else}
                                        <tr>
                                          <td colspan="5">{anomaly}</td>
                                        </tr>
                                      {/if}
                                    {/each}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        {/if}
                      {:else}
                        <div class="card">
                          <div class="card-body">
                            <h6 class="card-title">Analysis Results</h6>
                            <pre class="mb-0"><code>{formatObject(analysis.results)}</code></pre>
                          </div>
                        </div>
                      {/if}
                    </div>
                  </td>
                </tr>
              {/if}
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </div>
</div>

<style>
  .analysis-panel {
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
  
  .analysis-details {
    padding: 0.5rem;
    background-color: #f8f9fa;
    border-radius: 4px;
  }
  
  .analysis-details-row {
    background-color: #f8f9fa;
  }
  
  /* Style for the selected row */
  :global(.analysis-panel .table-info) {
    background-color: rgba(23, 162, 184, 0.1) !important;
  }
  
  /* Styling for statistics cards */
  .stat-card {
    display: flex;
    flex-direction: column;
    background-color: #fff;
    padding: 0.75rem;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  .stat-title {
    font-size: 0.8rem;
    color: #6c757d;
    margin-bottom: 0.25rem;
  }
  
  .stat-value {
    font-size: 1.1rem;
    font-weight: 600;
  }
  
  /* Code block styling */
  pre {
    background-color: #f1f1f1;
    padding: 1rem;
    border-radius: 4px;
    overflow-x: auto;
    font-size: 0.85rem;
  }
</style>
